import React from "react";
import { UseFormReturn, useWatch, Controller } from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2 } from "lucide-react";
import { SubmissionFormValues } from "./SubmissionForm";
import { PackagingMaterial, PackagingType, PlasticType } from "@/types";

// --- Types ---

export interface Commodity {
  id: string;
  productName: string;
  packagingType: string;
  packagingMaterials: PackagingMaterial[];
  isFullyEncapsulated: boolean;
  canBeReconfigured?: boolean;
  reconfigurationNotes?: string;
  plasticTypes?: PlasticType[];
}

export interface CommodityCardProps {
  index: number;
  form: UseFormReturn<SubmissionFormValues>;
  removeCommodity: (index: number) => void;
  packagingMaterials: { value: PackagingMaterial; label: string }[];
  packagingTypes: { value: string; label: string }[];
  plasticTypes: { value: PlasticType; label: string }[];
}

export const CommodityCard: React.FC<CommodityCardProps> = ({
  index,
  form,
  removeCommodity,
  packagingMaterials,
  packagingTypes,
  plasticTypes,
}) => {
  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = form;

  // subscribe to packagingMaterials for this commodity
  const currentMaterials = useWatch({
    control,
    name: `commodities.${index}.packagingMaterials`,
    defaultValue: [] as PackagingMaterial[],
  }) as PackagingMaterial[];

  // subscribe to encapsulation flag
  const isEncapsulated = useWatch({
    control,
    name: `commodities.${index}.isFullyEncapsulated`,
    defaultValue: false,
  }) as boolean;

  // subscribe to full commodity data for conditional fields
  const commodity = watch(`commodities.${index}`) as Commodity;

  return (
    <Card key={commodity.id} className="mb-4">
      <CardContent className="pt-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">Commodity #{index + 1}</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeCommodity(index)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4">
          {/* Product Name */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`commodity.${index}.productName`}>
                Product Stored <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                id={`commodity.${index}.productName`}
                {...register(`commodities.${index}.productName`, {
                  required: "Product name is required",
                })}
              />
              {errors?.commodities?.[index]?.productName && (
                <p className="text-red-500">
                  {errors.commodities[index]?.productName?.message}
                </p>
              )}
            </div>

            {/* Packaging Type */}
            <div className="space-y-2">
              <Label htmlFor={`commodity-${index}-packagingType`}>
                Packaging Type <span className="text-red-500 ml-1">*</span>
              </Label>
              <Controller
                control={control}
                name={`commodities.${index}.packagingType`}
                defaultValue={"plastic" as PackagingType}
                rules={{ required: "Select a packaging type" }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id={`commodity-${index}-packagingType`}>
                      <SelectValue placeholder="Choose type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {packagingTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors?.commodities?.[index]?.packagingType && (
                <p className="text-red-500">
                  {errors.commodities[index]?.packagingType?.message}
                </p>
              )}
            </div>
          </div>

          {/* Packaging Materials */}
          <div className="space-y-2">
            <Label>
              Packaging Material <span className="text-red-500 ml-1">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {packagingMaterials.map((material) => (
                <div
                  key={material.value}
                  className="flex items-center space-x-2"
                >
                  <Checkbox
                    id={`commodity-${index}-material-${material.value}`}
                    checked={currentMaterials.includes(material.value)}
                    onCheckedChange={(checked) => {
                      const updated = checked
                        ? [...currentMaterials, material.value]
                        : currentMaterials.filter((m) => m !== material.value);
                      setValue(
                        `commodities.${index}.packagingMaterials`,
                        updated,
                        { shouldValidate: true, shouldDirty: true }
                      );
                    }}
                    {...form.register(
                      `commodities.${index}.packagingMaterials`
                    )}
                  />
                  <Label
                    htmlFor={`commodity-${index}-material-${material.value}`}
                  >
                    {material.label}
                  </Label>
                </div>
              ))}
              {errors?.commodities &&
                errors?.commodities.length > 0 &&
                errors?.commodities[0]?.packagingMaterials && (
                  <p className="text-red-500">
                    {errors.commodities[0]?.packagingMaterials?.message}
                  </p>
                )}
            </div>
          </div>

          {/* Encapsulation Toggle */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Controller
                control={control}
                name={`commodities.${index}.isFullyEncapsulated`}
                defaultValue={false}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(val) => field.onChange(!!val)}
                    id={`commodity-${index}-encapsulated`}
                  />
                )}
              />
              <Label htmlFor={`commodity-${index}-encapsulated`}>
                Is packaging fully encapsulated (shrink-wrapped)?
              </Label>
            </div>
          </div>

          {/* Reconfigure Section */}
          {isEncapsulated && (
            <div className="space-y-2 ml-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`commodity-${index}-reconfigured`}
                  checked={!!commodity.canBeReconfigured}
                  onCheckedChange={(checked) =>
                    setValue(
                      `commodities.${index}.canBeReconfigured`,
                      !!checked,
                      { shouldDirty: true }
                    )
                  }
                />
                <Label htmlFor={`commodity-${index}-reconfigured`}>
                  Can it be reconfigured to non-encapsulated?
                </Label>
              </div>

              {commodity.canBeReconfigured && (
                <div className="space-y-2">
                  <Label htmlFor={`commodity-${index}-reconfigured-notes`}>
                    Notes
                  </Label>
                  <Input
                    id={`commodity-${index}-reconfigured-notes`}
                    value={commodity.reconfigurationNotes || ""}
                    onChange={(e) =>
                      setValue(
                        `commodities.${index}.reconfigurationNotes`,
                        e.target.value,
                        { shouldValidate: true, shouldDirty: true }
                      )
                    }
                    {...form.register(
                      `commodities.${index}.reconfigurationNotes`
                    )}
                  />
                  {errors?.commodities?.[index]?.reconfigurationNotes && (
                    <p className="text-red-500">
                      {errors.commodities[index]?.reconfigurationNotes?.message}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Plastic Types */}
          {currentMaterials.includes("plastic") && (
            <div className="space-y-2">
              <Label>Plastic Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {plasticTypes.map((type) => {
                  const selected = commodity.plasticTypes || [];
                  return (
                    <div
                      key={type.value}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`commodity-${index}-plastic-${type.value}`}
                        checked={selected.includes(type.value)}
                        onCheckedChange={(checked) => {
                          const updated = checked
                            ? [...selected, type.value]
                            : selected.filter((v) => v !== type.value);
                          setValue(
                            `commodities.${index}.plasticTypes`,
                            updated,
                            { shouldValidate: true, shouldDirty: true }
                          );
                        }}
                        {...register(`commodities.${index}.plasticTypes`)}
                      />
                      <Label
                        htmlFor={`commodity-${index}-plastic-${type.value}`}
                      >
                        {type.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
              {errors?.commodities?.[index]?.plasticTypes && (
                <p className="text-red-500">
                  {errors.commodities[index]?.plasticTypes?.message}
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
